"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="150325" className="flex gap-4">
        <div className="h-5 w-16 animate-pulse rounded-md bg-muted/20" />
        <div className="h-5 w-20 animate-pulse rounded-md bg-muted/20" />
        <div className="h-5 w-16 animate-pulse rounded-md bg-muted/20" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-sans text-xs font-bold uppercase tracking-[0.2em] text-white/80 transition-colors hover:text-[#3CC5F3] focus:text-[#3CC5F3]">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  setIsOpen
}: {
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        <div className="h-8 w-32 animate-pulse rounded-md bg-muted/20" />
        <div className="h-8 w-40 animate-pulse rounded-md bg-muted/20" />
        <div className="h-8 w-24 animate-pulse rounded-md bg-muted/20" />
      </div>;
  }
  return <nav className="flex flex-col gap-4 p-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="font-sans text-2xl font-black uppercase tracking-tighter text-white transition-colors hover:text-[#3CC5F3]" onClick={() => setIsOpen(false)}>
          {item.label}
        </Link>)}
      <div className="mt-6 flex flex-col gap-4">
        <Button className="w-full border-none bg-[#FCB231] font-sans font-bold uppercase tracking-wider text-[#0D1B2A] shadow-[0_0_20px_rgba(252,178,49,0.3)] hover:bg-[#FCB231]/90" asChild>
          <Link to="/PocketPalPage" onClick={() => setIsOpen(false)}>
            Talk to PocketPal
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);
  return <>
      <header id="header" className={cn("dark sticky top-0 z-40 w-full text-foreground transition-all duration-500", isScrolled ? "bg-[#0D1B2A]/70 backdrop-blur-lg" : "bg-[#0D1B2A]")}>
        <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6 lg:px-8">
          {}
          <div className="flex shrink-0 items-center">
            <Link to="/" className="flex items-center">
              <img src="https://workable-bull-1.10web.cloud/wp-content/uploads/2026/04/PocketBroker_LogoHorizontal_4c-reverse-scaled.png" alt="Pocket Broker" className="object-contain brightness-0 invert !w-[180px] !h-[47px] !text-transparent" />
            </Link>
          </div>

          {}
          <div className="hidden flex-1 items-center justify-center lg:flex">
            <MenuProvider menu_id="40">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="hidden shrink-0 items-center justify-end lg:flex">
            <Button className="border-none bg-[#FCB231] font-sans text-xs font-black uppercase tracking-widest text-[#0D1B2A] shadow-[0_0_20px_rgba(252,178,49,0.2)] transition-all duration-300 hover:shadow-[0_0_35px_rgba(252,178,49,0.5)] hover:opacity-100" asChild>
              <Link to="/PocketPalPage" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-9 px-4 py-2 has-[>svg]:px-3 border-none bg-[#FCB231] font-sans text-xs font-black uppercase tracking-widest shadow-[0_0_20px_rgba(252,178,49,0.2)] transition-all duration-300 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-9 px-4 py-2 has-[>svg]:px-3 border-none bg-[#FCB231] font-sans text-xs font-black uppercase tracking-widest shadow-[0_0_20px_rgba(252,178,49,0.2)] transition-all duration-300 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-9 px-4 py-2 has-[>svg]:px-3 border-none bg-[#FCB231] font-sans text-xs font-black uppercase tracking-widest shadow-[0_0_20px_rgba(252,178,49,0.2)] transition-all duration-300 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-9 px-4 py-2 has-[>svg]:px-3 border-none bg-[#FCB231] font-sans text-xs font-black uppercase tracking-widest shadow-[0_0_20px_rgba(252,178,49,0.2)] transition-all duration-300 disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 hover:shadow-[0_0_35px_rgba(252,178,49,0.5)] hover:opacity-100 disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 hover:shadow-[0_0_35px_rgba(252,178,49,0.5)] hover:opacity-100 disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 hover:shadow-[0_0_35px_rgba(252,178,49,0.5)] hover:opacity-100 disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 hover:shadow-[0_0_35px_rgba(252,178,49,0.5)] hover:opacity-100">Talk to PocketPal</Link>
            </Button>
          </div>

          {}
          <div className="flex items-center lg:hidden">
            <Button variant="ghost" size="icon" className="text-white hover:bg-[#3CC5F3]/10" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
              <Menu className="size-6" />
            </Button>
          </div>
        </div>

        {}
        <div className={cn("absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#3CC5F3] to-transparent transition-opacity duration-500", isScrolled ? "opacity-50" : "opacity-0")} />
      </header>

      {}
      {isMobileMenuOpen && <div className="dark fixed inset-0 z-50 flex flex-col bg-[#0D1B2A] text-white">
          <div className="flex h-16 items-center justify-between px-4 md:px-6">
            <Link to="/" className="flex items-center" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612503/img/pocket_broker_logo_horizontal.png" alt="Pocket Broker" className="h-7 w-auto object-contain brightness-0 invert" />
            </Link>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/5" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu">
              <X className="size-6" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <MenuProvider menu_id="40">
              <MobileMenuConsumer setIsOpen={setIsMobileMenuOpen} />
            </MenuProvider>
          </div>
        </div>}
    </>;
}